import { InputNumber, TableProps } from "antd"
import {
  SB_PLACEMENT_MODIFIER_LIMITS,
  SB_PLACEMENT_ROW_NAMES,
} from "../constants/placements.constants"
import {
  columnsWidth,
  PLACEMENT_LEADING_SPACER_WIDTH,
  placementBidColumnsWidth,
} from "../constants/columns.constants"
import { ISbPlacementRowForTable } from "../types/sbPlacement.types"
import SbSelectAudience from "../components/SbSelectAudience"

function resolveModifierMin(row: ISbPlacementRowForTable): number {
  if (row.placement === SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP) {
    return SB_PLACEMENT_MODIFIER_LIMITS.OTHER_THAN_TOP_MIN
  }
  return SB_PLACEMENT_MODIFIER_LIMITS.TOP_OF_SEARCH_MIN
}

export const useSbPlacementsColumns = (
  changeModifier: (placement: string, value: number) => void,
  changeTargetAcos: (placement: string, value: number) => void,
  changeAudienceId: (audienceId: string) => void,
  audienceId: string | null
): TableProps<ISbPlacementRowForTable>["columns"] => {
  const columns: TableProps<ISbPlacementRowForTable>["columns"] = [
    {
      // Mirrors the parent table's rowSelection + expand columns so the
      // placement grid starts where the parent's first data column starts.
      title: "",
      key: "leadingSpacer",
      width: PLACEMENT_LEADING_SPACER_WIDTH,
      render: () => null,
    },
    {
      title: "Target ACOS",
      key: "targetAcos",
      align: "center",
      width: columnsWidth[0],
      render: (row: ISbPlacementRowForTable) => (
        <InputNumber
          value={row.targetAcos}
          onChange={(value) => changeTargetAcos(row.placement, Number(value))}
          style={{ textAlign: "center" }}
          min={0}
          addonAfter="%"
        />
      ),
    },
    {
      // Parent "Active" slot has no placement equivalent; keep the track empty.
      title: "",
      key: "activeSpacer",
      width: columnsWidth[1],
      render: () => null,
    },
    {
      title: "Placement",
      key: "placement",
      align: "center",
      width: columnsWidth[2],
      render: (row: ISbPlacementRowForTable) =>
        row.isAudience ? (
          <SbSelectAudience audienceId={audienceId} onChange={changeAudienceId} />
        ) : (
          row.placement
        ),
    },
    {
      title: "Current Modifier",
      key: "currentModifier",
      align: "center",
      width: placementBidColumnsWidth[0],
      render: (row: ISbPlacementRowForTable) =>
        row.currentModifier == null ? "-" : row.currentModifier + "%",
    },
    {
      title: "Modifier",
      key: "modifier",
      align: "center",
      width: placementBidColumnsWidth[1],
      render: (row: ISbPlacementRowForTable) => (
        <InputNumber
          onChange={(value) => changeModifier(row.placement, Number(value))}
          style={{ textAlign: "center" }}
          value={row.modifier ?? (row.isAudience ? null : 0)}
          min={resolveModifierMin(row)}
          max={SB_PLACEMENT_MODIFIER_LIMITS.MAX}
          addonAfter="%"
          disabled={!row.isModifierEditable}
        />
      ),
    },
    {
      title: "Base Bid",
      key: "baseBid",
      align: "center",
      width: placementBidColumnsWidth[2],
      render: (row: ISbPlacementRowForTable) => (row.baseBid == null ? "-" : "$" + row.baseBid),
    },
    {
      title: "Placement Bid",
      key: "placementBid",
      align: "center",
      width: placementBidColumnsWidth[3],
      render: (row: ISbPlacementRowForTable) =>
        row.placementBid == null ? "-" : "$" + row.placementBid,
    },
    {
      title: "Current Bid",
      key: "currentBid",
      align: "center",
      width: placementBidColumnsWidth[4],
      render: (row: ISbPlacementRowForTable) =>
        row.currentPlacementBid == null ? "-" : "$" + row.currentPlacementBid,
    },
    {
      title: "Impressions",
      dataIndex: "impressions",
      key: "impressions",
      align: "center",
      width: columnsWidth[8],
    },
    {
      title: "Clicks",
      dataIndex: "clicks",
      key: "clicks",
      align: "center",
      width: columnsWidth[9],
    },
    {
      title: "CTR",
      dataIndex: "ctr",
      key: "ctr",
      align: "center",
      width: columnsWidth[10],
      render: (ctr) => ctr + "%",
    },
    {
      title: "Spend",
      dataIndex: "spend",
      key: "spend",
      align: "center",
      width: columnsWidth[11],
      render: (spend) => "$" + spend,
    },
    {
      title: "Sales",
      dataIndex: "sales",
      key: "sales",
      align: "center",
      width: columnsWidth[12],
      render: (sales) => "$" + sales,
    },
    {
      title: "Orders",
      dataIndex: "orders",
      key: "orders",
      align: "center",
      width: columnsWidth[13],
    },
    {
      title: "Units",
      dataIndex: "units",
      key: "units",
      align: "center",
      width: columnsWidth[14],
    },
    {
      title: "CVR",
      dataIndex: "cvr",
      key: "cvr",
      align: "center",
      width: columnsWidth[15],
      render: (cvr) => cvr + "%",
    },
    {
      title: "ACOS",
      dataIndex: "acos",
      key: "acos",
      align: "center",
      width: columnsWidth[16],
      render: (acos) => acos + "%",
    },
    {
      title: "CPC",
      dataIndex: "cpc",
      key: "cpc",
      align: "center",
      width: columnsWidth[17],
      render: (cpc) => "$" + cpc,
    },
    {
      title: "ROAS",
      dataIndex: "roas",
      key: "roas",
      align: "center",
      width: columnsWidth[18],
    },
  ]

  return columns
}
